import React, { useState } from 'react';
import ApiKeyModal from './ApiKeyModal';
import { FiEdit } from 'react-icons/fi';
import formatDate from 'utilities/FormatDate';

interface ApiKey {
  apiKey: string;
  description: string;
  permissions: string;
  expiryDate: string;
  createdDate: string;
  generatedOn: string;
}

const ApiKeyManager: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([
    {
      apiKey: 'abc123xyz',
      description: 'API key for read access to user data',
      permissions: 'read',
      expiryDate: '2025-12-31',
      createdDate: formatDate(new Date()),
      generatedOn: '13/05/2024',
    },
    {
      apiKey: 'def456uvw',
      description: 'API key for write access to settings',
      permissions: 'write',
      expiryDate: '2026-05-31',
      createdDate: formatDate(new Date()),
      generatedOn: '10/03/2024',
    }
  ]);
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);

  const handleAddClick = () => {
    setEditingKey(null);
    setIsApiKeyModalOpen(true);
  };

  const handleEditClick = (key: ApiKey) => {
    setEditingKey(key);
    setIsApiKeyModalOpen(true);
  };

  const handleSaveKey = (data: ApiKey) => {
    if (editingKey) {
      // Edit existing
      setApiKeys(prev =>
        prev.map(k => (k.apiKey === editingKey.apiKey ? data : k))
      );
    } else {
      // Add new
      setApiKeys(prev => [...prev, data]);
    }
    setIsApiKeyModalOpen(false);
    setEditingKey(null);
  };

  const [currentPage, setCurrentPage] = useState(1); // starts from 1
  const itemsPerPage = 4; // you can change this number

  const totalPages = Math.ceil(apiKeys.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const CurrentApiKeys = apiKeys.slice(startIndex, endIndex);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
      <h2 className="text-lg font-semibold text-white ">API access</h2>
        <button
          className="flex items-center gap-2 px-4 py-2 bg-[#2A2A2A] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors"
          onClick={handleAddClick}
        >
          + Generate API Key
        </button>
      </div>

      <div className="mt-4 bg-[#2A2A2A] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
              <th className="text-left p-4 font-medium">API key</th>
              <th className="text-left p-4 font-medium">Created date</th>
              <th className="text-left p-4 font-medium">Permissions</th>
              <th className="text-left p-4 font-medium">Generated on</th>
              <th className="text-right p-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {CurrentApiKeys.map((key, index) => (
              <tr key={index} className="border-b border-white/[0.08] hover:bg-[#3a3a3a33]">
                <td className="p-4 text-gray-400">{key.apiKey}</td>
                <td className="p-4 text-gray-400">{key.createdDate}</td>
                <td className="p-4 text-gray-400">{key.permissions}</td>
                <td className="p-4 text-gray-400">{key.generatedOn}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEditClick(key)}
                    className="text-blue-500 hover:text-blue-400"
                  >
                    <FiEdit size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
        <div>
          Showing {startIndex + 1}-{Math.min(endIndex, apiKeys.length)} from {apiKeys.length}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 hover:text-white disabled:opacity-50"
          >
            ←
          </button>
          {pages.map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-8 h-8 rounded flex items-center justify-center ${currentPage === page
                ? 'bg-[#3A3A3A] text-white'
                : 'hover:text-white'
                }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 hover:text-white disabled:opacity-50"
          >
            →
          </button>
        </div>

      </div>

      <ApiKeyModal
        isOpen={isApiKeyModalOpen}
        onClose={() => {
          setIsApiKeyModalOpen(false);
          setEditingKey(null);
        }}
        onSave={handleSaveKey}
        initialData={editingKey}
      />
    </div>
  );
};

export default ApiKeyManager;
